<script lang="ts" setup name="OrganisationPortCallsActivityWidgets">
import { computed, ref } from 'vue';

import {
  PORT_CALLS_ACTIVITY_WIDGET_TYPES,
  PORT_CALLS_ACTIVITY_WIDGET_TYPES_TO_TITLE,
} from '~/constants/organisationPortCalls';

import { provideBaseCardState } from '~/features/useBaseCardState';
import { formatNumber } from '~/features/useNumbers';

import Service from '~/services/Service.js';

import BaseDisplayMetric from '~/components/BaseDisplayMetric.vue';
import ComponentErrorCard from '~/components/ComponentErrorCard.vue';

import {
  type IPortCallsActivityWidgetTypes,
  type IPortCallsDetailsActivityWidgets,
  type IPortCallsDetailsActivityWidgetsResponse,
} from '~/types';

const { organisationId, portId } = defineProps<{
  organisationId: OrganisationId;
  portId: string;
}>();

const widgetsData = ref<IPortCallsDetailsActivityWidgets>();

const {
  setStateLoading,
  setStateIdle,
  setStateError,
  setStateNoData,
  stateIsError,
  stateIsNoData,
  stateIsLoading,
} = provideBaseCardState('PORT_CALLS_ACTIVITY_WIDGETS');

const fetchData = () => {
  Service.organisation(organisationId)
    .portCallsCalls()
    .activities(portId)
    .onStart(setStateLoading)
    .onError(setStateError)
    .onNoData(setStateNoData)
    .onSuccess(({ data }: IPortCallsDetailsActivityWidgetsResponse) => {
      widgetsData.value = data;
      setStateIdle();
    })
    .execute();
};

const handleRetry = () => {
  fetchData();
};

fetchData();

const toWidget = (key: IPortCallsActivityWidgetTypes) => {
  return {
    id: key as string,
    title: PORT_CALLS_ACTIVITY_WIDGET_TYPES_TO_TITLE[key] as string,
    subTitle: 'Activity At Port',
    value: formatNumber(widgetsData.value?.[key]?.value ?? 0, {
      maximumFractionDigits: 0,
    }),
    blinker: widgetsData.value?.[key]?.isActive,
    description: 'MT',
  };
};

const items = computed(() => PORT_CALLS_ACTIVITY_WIDGET_TYPES.map(toWidget));
</script>

<template>
  <ComponentErrorCard v-if="stateIsError" styled @retry="handleRetry" />

  <div v-else class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
    <template v-if="stateIsNoData">
      <BaseDisplayMetric
        v-for="item in items"
        :key="item.id"
        v-bind="item"
        :loading="stateIsLoading"
      />
    </template>

    <template v-else>
      <BaseDisplayMetric
        v-for="item in items"
        :key="item.id"
        v-bind="item"
        :loading="stateIsLoading"
      />
    </template>
  </div>
</template>
